/**
 * I-GTCC-47 — eine eigene URL je Cockpit-View (Register Z47).
 *
 * Der bestehende `?gtcc=<viewKey>`-Vertrag bleibt als Legacy-Adresse
 * les- und navigierbar. Zusaetzlich uebernimmt I-GTCC-47 den bereits auf
 * `lane-h/H-GTCC-47` belegten Pfadvertrag:
 *
 *   root build   `/gtcc/<viewKey>`
 *   admin build  `/admin/gtcc/<viewKey>`
 *
 * Eine direkt geladene Pfad-Adresse bleibt im Pfadmodus; Navigation und
 * Browser-Zurueck wechseln dann ebenfalls zwischen diesen Pfaden. Das
 * vermeidet einen harten Bruch vorhandener Query-Bookmarks und doppelte
 * Routing-Implementierungen.
 *
 * C-GTCC-VIEW-OVW1: die reine URL-Mechanik liegt in `deep-link-url.ts`. Dieses
 * Modul ist die Schicht, die einen Schluessel gegen die Registry prueft — und
 * damit das einzige der beiden, das die Registry importiert. Views, die nur
 * navigieren, nehmen das Blatt-Modul und erzeugen keinen Importzyklus.
 */
import {
  GTCC_UNKNOWN_VIEW_FALLBACK_KEY,
  GTCC_VIEW_PARAM,
  gtccPathForResolvedViewKey,
  gtccRequestedPathKey,
  gtccUrlForViewKey,
  writeGtccViewKeyToLocation as writeViewKeyToLocation,
  type GtccBrowserLocation,
} from "./deep-link-url.js";
import { GTCC_DEFAULT_VIEW_KEY, gtccViewByKey } from "./registry.js";

export {
  GTCC_UNKNOWN_VIEW_FALLBACK_KEY,
  GTCC_VIEW_PARAM,
  gtccUrlForViewKey,
  type GtccBrowserLocation,
};

/** Ein unbekannter Schluessel landet auf der Uebersicht. */
function resolveViewKey(key: string): string {
  return gtccViewByKey(key) ? key : GTCC_UNKNOWN_VIEW_FALLBACK_KEY;
}

/** Legacy query URL -> View. */
export function gtccViewKeyFromSearch(search: string): string {
  const requested = new URLSearchParams(search).get(GTCC_VIEW_PARAM);
  if (requested !== null && gtccViewByKey(requested)) return requested;
  return GTCC_DEFAULT_VIEW_KEY;
}

/** Full location -> View, with the canonical path taking precedence. */
export function gtccViewKeyFromLocation(
  location: GtccBrowserLocation,
  baseUrl: string | undefined,
): string {
  const pathKey = gtccRequestedPathKey(location.pathname, baseUrl);
  if (pathKey === null) return gtccViewKeyFromSearch(location.search);
  return gtccViewByKey(pathKey) ? pathKey : GTCC_UNKNOWN_VIEW_FALLBACK_KEY;
}

/** Canonical View -> build-base-aware path, retaining unrelated query state. */
export function gtccPathForViewKey(
  location: GtccBrowserLocation,
  key: string,
  baseUrl: string | undefined,
): string {
  return gtccPathForResolvedViewKey(location, resolveViewKey(key), baseUrl);
}

/** Die View, die die aktuelle Adresse benennt. */
export function readGtccViewKeyFromLocation(baseUrl?: string): string {
  if (typeof window === "undefined") return GTCC_DEFAULT_VIEW_KEY;
  return gtccViewKeyFromLocation(window.location, baseUrl);
}

/**
 * Schreibt die View in die Adresse. Eine Pfad-Adresse bleibt im Pfadmodus;
 * eine Legacy-Query-Adresse behaelt ihr kompatibles Schema.
 */
export function writeGtccViewKeyToLocation(key: string, replace = false, baseUrl?: string): void {
  writeViewKeyToLocation(key, replace, baseUrl, resolveViewKey);
}
